import type { ReactElement } from 'react';

/* Two small restatements of a figure that is already on screen: a trend line, and a split bar.
 *
 * **Both are restatements, never the only place a number appears.** That is the rule that keeps them
 * honest: each sits under a figure the card has already stated in words, so a reader who cannot see
 * the shape has lost nothing, and neither carries a value that is not written out beside it. A KPI
 * card whose only answer is a 40px graphic is a card nobody can quote.
 *
 * **Neither invents a data point.** `Sparkline` refuses a series of fewer than two — one measurement
 * is not a trend, and drawing a flat line through it would assert stability nobody measured, which
 * is this product's absence-is-not-zero rule applied to a picture. `SegmentBar` renders only the
 * segments it is given and reports the remainder as a distinct, hatched track rather than filling it
 * with the last colour to hand.
 *
 * **`Sparkline` is an extraction, not a new component, and the stylesheet gate is what said so.**
 * `HomeScreen`'s trend panel had worked this arithmetic out inline — the same range scaling, the same
 * `preserveAspectRatio="none"`, the same `non-scaling-stroke` — and my first version of this file
 * declared a second `.lq-spark` beside it. `stylesheets.test.ts` refused the duplicate selector,
 * which is the right refusal for the right reason: two implementations of one shape are two answers
 * to "how is a flat series drawn", and both would pass their own review. The panel now renders
 * through this, which is what proves the extraction preserves its behaviour rather than merely
 * compiling.
 *
 * The two sizes are a modifier rather than a prop-driven height, so the heights live in the
 * stylesheet with every other length in this product.
 */

/** A point on a trend. `value` only — the caller has already decided the order. */
export interface SparkPoint {
  readonly value: number;
}

export interface SparklineProps {
  readonly points: readonly SparkPoint[];
  /** Required. A `role="img"` with no name is an unlabelled graphic, and this one carries the shape
   * of a movement the caption states only as a single delta. */
  readonly label: string;
  /** `panel` is the 72px chart in a card of its own; `inline` is the 28px restatement under a KPI
   * figure. Default `inline`, because that is the constrained one — a caller who forgets gets the
   * size that fits anywhere rather than one that overflows a metric card. */
  readonly size?: 'panel' | 'inline';
}

/* The drawing box, in the SVG's own user units. Not pixels: the element is sized by CSS and this is
 * the coordinate space the path is computed in, so the two are deliberately independent — a wider
 * card stretches the line rather than re-deriving it. */
const VIEW_WIDTH = 100;
const VIEW_HEIGHT = 28;
/* Half the stroke, so the extreme points are not clipped by the viewBox edge. A 2px stroke centred
 * on y=0 loses its top half; every sparkline written without this looks flat at its peak. */
const INSET = 1.5;

/** A trend line. Scaled to its own range, so a series that moves 0.4 points is still legible. */
export function Sparkline({
  points,
  label,
  size = 'inline',
}: SparklineProps): ReactElement | null {
  /* **Two points is the floor, and returning `null` is the honest answer below it.** A single
   * measurement drawn as a line is a horizontal rule, which reads as "no change" — a claim about
   * data that has only been looked at once. */
  if (points.length < 2) return null;

  const values = points.map((point) => point.value);
  const low = Math.min(...values);
  const high = Math.max(...values);
  /* **A flat series is drawn through the middle rather than divided by zero.** `high === low` is a
   * real answer — an estate whose score has not moved — and it should render as a level line, which
   * is true rather than misleading here because the *caption* states the delta as 0. */
  const span = high - low || 1;

  const path = values
    .map((value, index) => {
      const x = (index / (values.length - 1)) * VIEW_WIDTH;
      /* Inverted: SVG's y grows downward and a score does not. */
      const y = VIEW_HEIGHT - INSET - ((value - low) / span) * (VIEW_HEIGHT - INSET * 2);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');

  /* The same line, closed down to the baseline and back — the wash under the panel chart.
   *
   * Built from the path rather than re-derived, so the fill cannot disagree with the stroke about
   * where a point sits. Only for the panel size: see `.lq-spark__area` for why 28px is too short for
   * a fill to describe anything. */
  const area =
    size === 'panel'
      ? `${path} L${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)} L0 ${String(VIEW_HEIGHT)} Z`
      : null;

  return (
    <svg
      className={size === 'panel' ? 'lq-spark lq-spark--panel' : 'lq-spark'}
      viewBox={`0 0 ${String(VIEW_WIDTH)} ${String(VIEW_HEIGHT)}`}
      /* `none`, so the line fills the card's width and its vertical proportions are the card's
         rather than the series'. A sparkline that preserved its aspect ratio would be a different
         height on every card, which is the one thing a row of four figures cannot have. */
      preserveAspectRatio="none"
      role="img"
      aria-label={label}
    >
      {area === null ? null : <path className="lq-spark__area" d={area} />}
      <path className="lq-spark__line" d={path} />
    </svg>
  );
}

/** One band of a split bar. `share` is a fraction of the whole, not a percentage. */
export interface Segment {
  readonly id: string;
  readonly share: number;
  /** A CSS colour, from a token. Named by the caller because what the bands *mean* is the caller's. */
  readonly colour: string;
}

export interface SegmentBarProps {
  readonly segments: readonly Segment[];
  readonly label: string;
  /** Whether the space the segments do not fill is drawn as a hatched remainder.
   *
   * **Off by default, and the distinction is the point.** A severity split *is* the whole — every
   * incident is one of three — so an unfilled tail would be a fourth category nobody named. A
   * coverage bar's tail is the opposite: the unmeasured datasets are the finding, and leaving them
   * as flat track would make an incomplete estate look like a complete one drawn short. */
  readonly hatchRemainder?: boolean;
}

export function SegmentBar({
  segments,
  label,
  hatchRemainder = false,
}: SegmentBarProps): ReactElement {
  const filled = segments.reduce((total, segment) => total + segment.share, 0);
  return (
    <span
      className={hatchRemainder ? 'lq-segbar lq-segbar--hatched' : 'lq-segbar'}
      role="img"
      aria-label={label}
    >
      {segments
        /* Zero-share bands are dropped rather than rendered at 0%: a band with no width still shows
           as a 1px seam against its neighbour, which reads as a category with one member. */
        .filter((segment) => segment.share > 0)
        .map((segment) => (
          <span
            key={segment.id}
            className="lq-segbar__band"
            style={{
              /* Clamped, because a caller's shares are data and a band wider than its track is a
                 rendering bug that looks like a measurement. */
              width: `${String(Math.max(0, Math.min(100, segment.share * 100)))}%`,
              background: segment.colour,
            }}
          />
        ))}
      {hatchRemainder && filled < 1 ? (
        <span
          className="lq-segbar__rest"
          style={{ width: `${String(Math.max(0, (1 - filled) * 100))}%` }}
        />
      ) : null}
    </span>
  );
}
